import { useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import InfoModal from 'components/Common/InfoModal';
import { notify } from 'components/Common/Toast';
import { InviteMemberProps } from 'types/modal';
import InviteMemberForm, { InviteMemberFormValues } from 'components/Community/InviteMemberForm';
import { createMemberInvitation } from 'services/memberInvitationsApi';
import { selectSelectedParishId } from 'redux/selectors/parishContext';
import { getProblemDetail } from 'api/v1Helpers';

const InviteMemberModal = ({ isOpen, onClose, onInvited }: InviteMemberProps) => {
    const parishId = useSelector(selectSelectedParishId);
    const [loading, setLoading] = useState(false);

    const onSubmit = async (formValues: InviteMemberFormValues) => {
        if (loading) return;
        if (!parishId) {
            notify('Select a parish before inviting a member.', 'error');
            return;
        }
        setLoading(true);
        try {
            const result = await createMemberInvitation(parishId, formValues);
            notify(
                result.kind === 'added'
                    ? 'Member added successfully. An onboarding email has been sent.'
                    : 'Member account created. An onboarding email has been sent.',
                'success'
            );
            onInvited?.();
            onClose();
        } catch (err) {
            const detail = getProblemDetail(err);
            notify(detail || 'Failed to create member', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <InfoModal title="Invite New Member" isOpen={isOpen} onClose={onClose} hideActionButtons contentAlign="left">
            <Stack gap={{ xs: 2, sm: 2.5 }} sx={{ width: '100%' }}>
                <Typography sx={{ fontSize: { xs: 14, sm: 16 }, color: '#1F2937', lineHeight: 1.5 }}>
                    Enter the parishioner&apos;s details. We&apos;ll create their account and email them sign-in instructions for the
                    Catholic Community mobile application.
                </Typography>
                <InviteMemberForm loading={loading} onSubmit={onSubmit} />
            </Stack>
        </InfoModal>
    );
};

export default InviteMemberModal;
